<template>
  <router-link
    v-if="userId"
    :to="target"
    class="user-link"
    :title="linkTitle"
  ><slot>{{ label || userId }}</slot></router-link>
  <span v-else class="user-link user-link--plain" :title="linkTitle"><slot>{{ label || '—' }}</slot></span>
</template>

<script>
/**
 * Clickable username used across every admin report.
 *
 * Opens the admin user profile (the User Activity drill-down inside
 * /admin-console), which is the same pane the Users table links to. The path is
 * absolute rather than a query merge, so it works from the standalone report
 * routes (/service-audit, /admin/search-analytics, …) as well as from inside
 * the console.
 *
 * Rows with no resolvable userID (guests, anonymous search/interaction events,
 * deleted users) render as plain text rather than a dead link.
 */
export default {
  name: "UserLink",
  props: {
    // Renants userID of the person. Empty/undefined renders plain text.
    // Numbers are accepted because some reports carry the id as-is from Mongo.
    userId: { type: [String, Number], default: "" },
    // Text to show. Defaults to the userID when omitted and no slot is given.
    label: { type: [String, Number], default: "" },
    // Tooltip; falls back to a helpful default when the link is live.
    title: { type: String, default: "" },
  },
  computed: {
    target() {
      const query = { section: "user-activity", userID: String(this.userId) };
      // Remember the pane (and its tab) this was clicked from, so the activity
      // page's back button returns there instead of always landing on the Users
      // list. Only meaningful inside the console — from a standalone report
      // route there is no section to go back to.
      // A bare /admin-console URL carries no ?section= yet still shows the
      // dashboard, which is exactly where this drill-down is clicked from most
      // often — so default it rather than treating it as "no origin".
      const from = this.$route?.query?.section || "dashboard";
      if (this.$route?.name === "admin-console" && from !== "user-activity") {
        query.backTo = String(from);
        if (this.$route.query.tab) query.backTab = String(this.$route.query.tab);
      }
      return { path: "/admin-console", query };
    },
    linkTitle() {
      if (this.title) return this.title;
      return this.userId ? `View activity for ${this.label || this.userId}` : "";
    },
  },
};
</script>

<style scoped>
.user-link {
  color: #3467d5;
  text-decoration: none;
  cursor: pointer;
  font-weight: 500;
}
.user-link:hover {
  text-decoration: underline;
}
/* No userID to link to — keep the layout identical, drop the affordance. */
.user-link--plain {
  color: inherit;
  cursor: default;
  font-weight: inherit;
}
</style>
